import moment from "moment";
import currency from "currency.js";
import chroma from "chroma-js";

export const formatPhoneNumber = (value) => {
  // if input value is falsy eg if the user deletes the input, then just return
  if (!value) return value;

  // clean the input for any non-digit values.
  const phoneNumber = value.replace(/[^\d]/g, "");

  // phoneNumberLength is used to know when to apply our formatting for the phone number
  const phoneNumberLength = phoneNumber.length;

  var match = phoneNumber.match(/^(\d{1,3}|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = match[1] ? `+${match[1]} ` : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  } else {
    return value;
  }
};

export function getQueryParam(queryStr, p) {
  const arr = queryStr.replace("?", "").split("&");
  for (const a of arr) {
    if (a.startsWith(`${p}=`)) {
      return decodeURIComponent(a.substring(p.length + 1));
    }
  }
  return undefined;
}

export function capitalize(s) {
  if (typeof s !== "string") return "";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function createSnippets(productType, shape, size, servings) {
  const data = [];
  if (!productType || productType === "Cake") {
    data.push(`${shape}, ${size} cake`);
    data.push(`${servings} servings`);
  } else {
    data.push(`${servings} ${productType}`);
  }

  return data;
}

export function getDisplayName(firstName, lastName, name) {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else if (firstName) {
    return firstName;
  } else {
    return name;
  }
}

export function isPWA() {
  return window.navigator.standalone || window.matchMedia("(display-mode: standalone)").matches;
}

export function getOS() {
  var userAgent = window.navigator.userAgent,
    platform = window.navigator?.userAgentData?.platform || window.navigator.platform,
    macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"],
    windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"],
    iosPlatforms = ["iPhone", "iPad", "iPod"],
    os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "Mac OS";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "iOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "Windows";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
  } else if (/Linux/.test(platform)) {
    os = "Linux";
  }

  return os;
}

export function getDefaultQuoteExpiration(eventDate) {
  if (eventDate) {
    const daysToEvent = moment(eventDate).diff(moment(), "days");
    if (daysToEvent < 3) {
      return moment().add(2, "hours");
    } else if (daysToEvent < 7) {
      return moment().add(1, "days");
    } else if (daysToEvent < 21) {
      return moment().add(2, "days");
    } else if (daysToEvent < 45) {
      return moment().add(3, "days");
    } else {
      return moment().add(5, "days");
    }
  } else {
    return moment().add(1, "days");
  }
}

export function getAmountWithTxnFee(amount) {
  return currency(amount).add(currency(amount).multiply(0.0472)).toString();
}

export function getAmountWithoutTxnFee(amount) {
  return currency(amount).divide(1.0472).toString();
}

export function getOriginalAmount(amount, includeFees) {
  return (includeFees ? getAmountWithoutTxnFee(amount) : amount) || "";
}

export function getStartTime(str) {
  let hour = 0;
  const time = (str?.trim() || "").toLowerCase();

  const amIdx = time.indexOf("am");
  const noonIdx = time.indexOf("noon");
  const pmIdx = time.indexOf("pm");

  // find first occurence
  const arr = [amIdx, noonIdx, pmIdx].filter((idx) => idx >= 0);
  if (arr.length > 0) {
    // split time string at the first match
    const splitIdx = Math.min(...arr);
    if (noonIdx === splitIdx) {
      hour = 12;
    } else {
      // split string and extract hour
      hour = parseInt(time.substring(0, splitIdx));
      if (pmIdx === splitIdx) {
        if (hour !== 12) {
          hour = hour + 12;
        }
      }
    }
  }

  return hour;
}

export function sortByOrderDateTime(o1, o2) {
  let result = 0;

  try {
    result = o1.fulfillment.date.localeCompare(o2.fulfillment.date);
    if (result === 0) {
      // if date is same, sort by time
      result = getStartTime(o1.fulfillment.time) - getStartTime(o2.fulfillment.time);
    }
  } catch (error) {
    console.log("invalid order date/time");
  }
  return result;
}

export function getCustomerName(customer) {
  if (customer.firstName) {
    return `${customer.firstName} ${customer.lastName || ""}`;
  } else if (customer.name && customer.lastName) {
    return `${customer.name} ${customer.lastName}`;
  } else {
    return customer.name || "";
  }
}

export function sortNotifications(o1, o2) {
  if (o1.status === o2.status) {
    if (o1.status === "open") {
      if (o1.priority > o2.priority) {
        return 1;
      } else if (o1.priority < o2.priority) {
        return -1;
      } else {
        return o1.link.localeCompare(o2.link);
      }
    } else {
      // closed status sorted by ts
      return o1.ts._seconds < o2.ts._seconds ? 1 : -1;
    }
  } else {
    return o1.status === "closed" ? 1 : -1;
  }
}

export function getVariantTotal(variant) {
  let total = 0;
  for (const o of Object.values(variant)) {
    if (o?.pricing) {
      total += +o.pricing.amount;
    } else if (typeof o === "object") {
      total += getVariantTotal(o);
    }
  }
  return +total;
}

export function generateSizeSummary(variant, productType) {
  let arr = [];

  if (productType) {
    arr.push(productType);
  }

  if (variant) {
    if (variant.name) {
      arr.push(variant.name);
    } else {
      if (variant.shape) {
        arr.push(variant.shape);
      }
      if (variant.size) {
        arr.push(variant.size);
      }
      if (variant.servings) {
        arr.push(`${variant.servings} servings`);
      }
    }
  }

  return arr.join(" • ");
}

export function getAddOnSummary(addOnVariant) {
  if (Array.isArray(addOnVariant)) {
    // old format: Delete
    return addOnVariant.map((o) => o.name).join(", ");
  } else {
    // New format
    return addOnVariant?.name;
  }
}

export function generateFlavorSummary(variant) {
  let summary = "";
  if (variant) {
    const flavor = normalizeFlavor(variant.flavor);

    if (flavor.name) {
      summary = `Flavor: ${flavor.name}`;
    }
    if (flavor.filling) {
      summary += ` • Filling: ${flavor.filling}`;
    } else if (variant.filling) {
      summary += ` • Filling: ${variant.filling.name}`;
    }
  }

  return summary;
}

export function getProductTypes(model) {
  return Object.keys(model);
}

export function getProductSubtypes(model, productType) {
  return Object.keys(model[productType]);
}

export function getItemMetadata(product) {
  let [minPrice, maxPrice, leadTime] = [0, 0, 0];
  let dietary = new Set();
  if (product.options?.size) {
    for (const variant of product.options.size) {
      const price = +variant.pricing.amount;
      if (minPrice === 0) {
        minPrice = price;
      } else if (price <= minPrice) {
        minPrice = price;
      } else if (price >= maxPrice) {
        maxPrice = price;
      }

      const title = variant.size?.toLowerCase();
      if (title.includes("vegan")) {
        dietary.add("VG");
      }

      if (title.includes("gluten") && title.includes("free")) {
        dietary.add("GF");
      }

      leadTime = product.rules?.date?.leadTime || 0;
    }
  }

  return { price: { min: minPrice, max: maxPrice }, dietary: Array.from(dietary), leadTime };
}

export function isTruncated(element) {
  if (element && (element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth)) {
    return true;
  } else {
    return false;
  }
}

export function normalizeFlavor(value) {
  if (Array.isArray(value)) {
    return value[0];
  } else if (typeof value === "object" && value.name) {
    // v2.0 format
    return value;
  } else if (typeof value === "object") {
    return { name: value?.base?.name, filling: value?.filling?.name };
  } else {
    return "";
  }
}

export function convertLineItemToOrderDetails(item) {
  let o = {
    decor: item.description || "",
    pics: item.images || [],
    productType: item.type,
    tags: item.tags || [],
    title: item.title,
    size: "",
    shape: "",
    servings: "",
    flavor: "",
    filling: "",
    frosting: "",
    inscription: ""
  };

  if (item.variant) {
    if (item.variant.size) {
      o.size = item.variant.size.size || "";
      o.shape = item.variant.size.shape || "";
      o.servings = item.variant.size.servings || "";
    }
    if (item.variant.flavor) {
      o.flavor = item.variant.flavor[0].name || "";
      o.filling = item.variant.flavor[0].filling || "";
    }
    if (item.variant.frosting) {
      o.frosting = item.variant.frosting.name || "";
    }
  }
  if (item.customization) {
    o.inscription = item.customization.inscription.text;
  }

  return o;
}

export function doObjectsMatch(o1, o2) {
  const replacer = (key, value) =>
    value instanceof Object && !(value instanceof Array)
      ? Object.keys(value)
          .sort()
          .reduce((sorted, key) => {
            sorted[key] = value[key];
            return sorted;
          }, {})
      : value;

  const j1 = JSON.stringify(o1, replacer);
  const j2 = JSON.stringify(o2, replacer);
  console.log("compare objects", j1, j2);

  return j1 === j2;
}

export function getNormalizedUtmSrc(utm) {
  let utmSrc = `${utm.utms}:${utm.utmc}`;

  // normalize utm
  if (utmSrc.includes("google")) {
    utmSrc = "google";
  } else if (utmSrc.includes("instagram")) {
    utmSrc = "instagram";
  } else if (utmSrc.includes("facebook")) {
    utmSrc = "facebook";
  } else if (utmSrc.includes("yelp")) {
    utmSrc = "yelp";
  } else if (utmSrc.includes("yahoo")) {
    utmSrc = "yahoo";
  }

  return utmSrc;
}

export function getFormattedAddressAsArray(location, includeUnit) {
  let arr = [];

  if (location) {
    let line1 = location.street;
    if (includeUnit && location.unit) {
      line1 += `, ${location.unit}`;
    }
    arr.push(line1);
    arr.push(`${location.city}, ${location.state} ${location.zip}`);
  }
  return arr;
}

export function isEmailValid(email) {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
}

export function getOptmizedImageUrl(imageUrl, quality, width) {
  if (imageUrl && imageUrl.includes("cloudinary.com") && imageUrl.includes("/upload/")) {
    return imageUrl.replace("/upload/", `/upload/f_auto,q_${quality},w_${width}/`);
  } else {
    return imageUrl;
  }
}

export function getStoreClosedDaysOfWeek(storeLocation) {
  let arr = [];
  for (const [key, value] of Object.entries(storeLocation.fulfillment?.pickup?.timeslots)) {
    if (value?.length === 0) {
      arr.push(key.toUpperCase());
    }
  }

  return arr;
}

export function isDateAvailable(d, storeLocation) {
  const date = moment(d);

  let timeslotsByDayOfWeek = storeLocation.fulfillment?.pickup?.timeslots?.[date.format("ddd").toLowerCase()] || [];
  if (timeslotsByDayOfWeek.length === 0) {
    return false;
  } else if (storeLocation.rules?.date?.block.includes(date.format("YYYY-MM-DD"))) {
    return false;
  }

  return true;
}

export function isTestBaker(bakerId) {
  const TEST_BAKERS = ["-MX2oaNrh4TcKVvnhcT9", "-MVIf1diaYOCm5KynVDF"];
  return TEST_BAKERS.includes(bakerId);
}

export function anyWordStartsWith(string, prefix) {
  string = string.toLowerCase();
  prefix = prefix.toLowerCase();

  if (string.startsWith(prefix)) {
    return true;
  }

  let tokens = string.toLowerCase().split(" ");
  for (const token of tokens) {
    if (token.trim().startsWith(prefix)) {
      return true;
    }
  }

  return false;
}

export function equalsIgnoreCase(str1, str2) {
  if (str1 && str2) {
    return str1.trim().toLowerCase() === str2.trim().toLowerCase();
  }
  return false;
}

export function getVariantOptionSurchargeForDisplay(option, basePrice) {
  let surcharge = "";
  if (+option.pricing?.amount > 0) {
    surcharge = currency(option.pricing?.amount).format();
  } else if (+basePrice > 0 && +option.pricing?.factor > 0) {
    surcharge = `${currency(basePrice).multiply(option.pricing?.factor).format()}`;
  } else if (+option.pricing?.factor > 0) {
    surcharge = `${(option.pricing.factor * 100).toFixed(2)}%`;
  }

  return surcharge;
}

export function printStringByWord(myString, myDiv) {
  var words = myString.split(" ");
  var index = 0;
  var intervalId = setInterval(function() {
      myDiv.innerHTML += words[index] + " ";
      index++;
      if(index == words.length) {
          clearInterval(intervalId);
      }
  }, 200);
}

export function getSizeChartByProductType(sizeChart, productType) {
  let arr = [];
  if (sizeChart && sizeChart[capitalize(productType)]) {
    for (const shape in sizeChart[capitalize(productType)]) {
      for (const sizeOption of sizeChart[capitalize(productType)][shape]) {
        const name = generateSizeSummary({ shape, ...sizeOption }, capitalize(productType));
        arr.push({
          id: `${shape}_${sizeOption.size || sizeOption.name}`,
          name: name,
          shape: shape,
          size: sizeOption.size || sizeOption.name,
          originalData: sizeOption
        });
      }
    }
  } else {
    ["Mini", "Small", "Standard", "Large", "Jumbo"].forEach((size) => {
      arr.push({
        id: `${size}`,
        name: size,
        size: size
      });
    });
  }

  return arr;
}

export function isSuperAdmin(uid) {
  return uid === "j2tO1dfIh9ZnUnb40p4lK60gXaI2" || uid === "hNGQFaTpqOYdtnWpoClwsHe49fq2";
}

export function getOptimizedCloudinaryUrl(originalUrl, transformations = 'f_auto,q_auto') {
  if (!originalUrl || typeof originalUrl !== 'string' || !originalUrl.includes('cloudinary.com')) {
    return originalUrl;
  }

  // Define the segment where transformations are typically inserted
  const uploadSegment = '/upload/';

  // Check if the URL is a standard Cloudinary URL that can be transformed
  if (originalUrl.includes(uploadSegment)) {
    // 1. Find the index right after '/upload/'
    const insertIndex = originalUrl.indexOf(uploadSegment) + uploadSegment.length;

    // 2. Insert the transformations string (e.g., 'f_auto,q_auto/')
    const optimizedUrl = 
      originalUrl.slice(0, insertIndex) + 
      transformations + 
      '/' + // Add a trailing slash after the transformations
      originalUrl.slice(insertIndex);

    return optimizedUrl;
  }

  // Return the original URL if the structure is unexpected
  return originalUrl;
}

/**
 * Generates a really soft, light background color based on a theme color.
 *
 * @param {string} themeColor - The base color (e.g., '#3498db', 'rgb(52, 152, 219)').
 * @param {number} saturation - The target saturation percentage (0-1). Lower is softer/grayer. Default is 0.2.
 * @param {number} lightness - The target lightness percentage (0-1). Higher is lighter/softer. Default is 0.95.
 * @returns {string} The soft background color in HEX format.
 */
export function getSoftBackgroundColor(themeColor, saturation = 0.2, lightness = 0.95) {
  // 1. Convert the theme color to the HSL space (Hue, Saturation, Lightness).
  // The 'hsv' or 'hsl' method is ideal for changing tone without losing the color identity (Hue).
  const softColor = chroma(themeColor)
    .hsl(); // Get [h, s, l] array

  // 2. Manipulate the Saturation (index 1) and Lightness (index 2).
  // chroma.hsl() returns an array [h, s, l] where s and l are 0-1.
  softColor[1] = saturation; // Reduce saturation to make it softer/muted
  softColor[2] = lightness; // Increase lightness to make it very faint

  // 3. Convert the new HSL array back to a HEX string for CSS use.
  return chroma(softColor, 'hsl').hex();
}

/**
 * Generates a random background pattern using SVG shapes.
 * Creates subtle, aesthetic patterns with various geometric shapes.
 *
 * @param {string} baseColor - The base color for the pattern (e.g., '#78374a').
 * @returns {string} A data URI string that can be used as a CSS background-image.
 */
export function generateRandomPattern(baseColor) {
  const patternId = `pattern-${Math.random().toString(36).substr(2, 9)}`;
  const width = 300;
  const height = 300;
  const opacity = 0.10 + Math.random() * 0.12; // Subtle but visible pattern
  
  // Randomly choose pattern type - more refined options
  const patternTypes = ['dots', 'subtleCircles', 'softWaves', 'fineGrid', 'organicDots', 'squares', 'triangles', 'hexagons', 'diamonds', 'stars'];
  const patternType = patternTypes[Math.floor(Math.random() * patternTypes.length)];
  
  let patternSvg = '';
  
  switch (patternType) {
    case 'dots':
      // Fine, evenly spaced dots
      const dotSize = 1 + Math.random() * 1.5;
      const dotSpacing = 30 + Math.random() * 20;
      const offsetX = Math.random() * dotSpacing;
      const offsetY = Math.random() * dotSpacing;
      patternSvg = Array.from({ length: Math.ceil(width / dotSpacing) * Math.ceil(height / dotSpacing) }, (_, i) => {
        const col = i % Math.ceil(width / dotSpacing);
        const row = Math.floor(i / Math.ceil(width / dotSpacing));
        const x = col * dotSpacing + offsetX;
        const y = row * dotSpacing + offsetY;
        if (x < width && y < height) {
          return `<circle cx="${x}" cy="${y}" r="${dotSize}" fill="${baseColor}" opacity="${opacity}"/>`;
        }
        return '';
      }).filter(Boolean).join('');
      break;
      
    case 'subtleCircles':
      // Large, very subtle concentric circles
      const numCircles = 2 + Math.floor(Math.random() * 3);
      patternSvg = Array.from({ length: numCircles }, () => {
        const cx = width * (0.2 + Math.random() * 0.6);
        const cy = height * (0.2 + Math.random() * 0.6);
        const r = 40 + Math.random() * 60;
        return `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${baseColor}" stroke-width="0.5" opacity="${opacity}"/>`;
      }).join('');
      break;
      
    case 'softWaves':
      // Gentle, flowing waves
      const numWaves = 2 + Math.floor(Math.random() * 2);
      patternSvg = Array.from({ length: numWaves }, (_, i) => {
        const y = (i + 1) * (height / (numWaves + 1));
        const amplitude = 5 + Math.random() * 8;
        const frequency = 0.015 + Math.random() * 0.025;
        let path = `M 0 ${y}`;
        for (let x = 0; x <= width; x += 3) {
          const waveY = y + Math.sin(x * frequency) * amplitude;
          path += ` L ${x} ${waveY}`;
        }
        return `<path d="${path}" fill="none" stroke="${baseColor}" stroke-width="0.5" opacity="${opacity}" stroke-linecap="round"/>`;
      }).join('');
      break;
      
    case 'fineGrid':
      // Very fine, subtle grid
      const gridSpacing = 40 + Math.random() * 30;
      patternSvg = '';
      // Vertical lines
      for (let x = 0; x <= width; x += gridSpacing) {
        patternSvg += `<line x1="${x}" y1="0" x2="${x}" y2="${height}" stroke="${baseColor}" stroke-width="0.5" opacity="${opacity}"/>`;
      }
      // Horizontal lines
      for (let y = 0; y <= height; y += gridSpacing) {
        patternSvg += `<line x1="0" y1="${y}" x2="${width}" y2="${y}" stroke="${baseColor}" stroke-width="0.5" opacity="${opacity}"/>`;
      }
      break;
      
    case 'organicDots':
      // Organic, slightly irregular dot pattern
      const organicDotSize = 1.5 + Math.random() * 1;
      const organicSpacing = 35 + Math.random() * 15;
      patternSvg = Array.from({ length: Math.ceil(width / organicSpacing) * Math.ceil(height / organicSpacing) }, (_, i) => {
        const col = i % Math.ceil(width / organicSpacing);
        const row = Math.floor(i / Math.ceil(width / organicSpacing));
        // Add slight randomness to position for organic feel
        const x = col * organicSpacing + (Math.random() - 0.5) * 5;
        const y = row * organicSpacing + (Math.random() - 0.5) * 5;
        if (x > 0 && x < width && y > 0 && y < height) {
          return `<circle cx="${x}" cy="${y}" r="${organicDotSize}" fill="${baseColor}" opacity="${opacity}"/>`;
        }
        return '';
      }).filter(Boolean).join('');
      break;
      
    case 'squares':
      // Subtle square pattern
      const squareSize = 8 + Math.random() * 12;
      const squareSpacing = 25 + Math.random() * 20;
      const squareOffsetX = Math.random() * squareSpacing;
      const squareOffsetY = Math.random() * squareSpacing;
      patternSvg = Array.from({ length: Math.ceil(width / squareSpacing) * Math.ceil(height / squareSpacing) }, (_, i) => {
        const col = i % Math.ceil(width / squareSpacing);
        const row = Math.floor(i / Math.ceil(width / squareSpacing));
        const x = col * squareSpacing + squareOffsetX;
        const y = row * squareSpacing + squareOffsetY;
        if (x < width && y < height) {
          const rotation = Math.random() * 45; // Random rotation up to 45 degrees
          return `<rect x="${x - squareSize/2}" y="${y - squareSize/2}" width="${squareSize}" height="${squareSize}" fill="none" stroke="${baseColor}" stroke-width="0.5" opacity="${opacity}" transform="rotate(${rotation} ${x} ${y})"/>`;
        }
        return '';
      }).filter(Boolean).join('');
      break;
      
    case 'triangles':
      // Triangle pattern
      const triangleSize = 10 + Math.random() * 15;
      const triangleSpacing = 30 + Math.random() * 20;
      const triangleOffsetX = Math.random() * triangleSpacing;
      const triangleOffsetY = Math.random() * triangleSpacing;
      patternSvg = Array.from({ length: Math.ceil(width / triangleSpacing) * Math.ceil(height / triangleSpacing) }, (_, i) => {
        const col = i % Math.ceil(width / triangleSpacing);
        const row = Math.floor(i / Math.ceil(width / triangleSpacing));
        const x = col * triangleSpacing + triangleOffsetX;
        const y = row * triangleSpacing + triangleOffsetY;
        if (x < width && y < height) {
          const rotation = Math.random() * 360;
          const h = triangleSize * Math.sqrt(3) / 2;
          const points = `${x},${y - h/2} ${x - triangleSize/2},${y + h/2} ${x + triangleSize/2},${y + h/2}`;
          return `<polygon points="${points}" fill="none" stroke="${baseColor}" stroke-width="0.5" opacity="${opacity}" transform="rotate(${rotation} ${x} ${y})"/>`;
        }
        return '';
      }).filter(Boolean).join('');
      break;
      
    case 'hexagons':
      // Hexagon pattern
      const hexSize = 12 + Math.random() * 10;
      const hexSpacing = 35 + Math.random() * 15;
      const hexOffsetX = Math.random() * hexSpacing;
      const hexOffsetY = Math.random() * hexSpacing;
      patternSvg = Array.from({ length: Math.ceil(width / hexSpacing) * Math.ceil(height / hexSpacing) }, (_, i) => {
        const col = i % Math.ceil(width / hexSpacing);
        const row = Math.floor(i / Math.ceil(width / hexSpacing));
        const x = col * hexSpacing + hexOffsetX;
        const y = row * hexSpacing + hexOffsetY;
        if (x < width && y < height) {
          const points = [];
          for (let j = 0; j < 6; j++) {
            const angle = (Math.PI / 3) * j;
            const px = x + hexSize * Math.cos(angle);
            const py = y + hexSize * Math.sin(angle);
            points.push(`${px},${py}`);
          }
          return `<polygon points="${points.join(' ')}" fill="none" stroke="${baseColor}" stroke-width="0.5" opacity="${opacity}"/>`;
        }
        return '';
      }).filter(Boolean).join('');
      break;
      
    case 'diamonds':
      // Diamond pattern
      const diamondSize = 8 + Math.random() * 12;
      const diamondSpacing = 25 + Math.random() * 20;
      const diamondOffsetX = Math.random() * diamondSpacing;
      const diamondOffsetY = Math.random() * diamondSpacing;
      patternSvg = Array.from({ length: Math.ceil(width / diamondSpacing) * Math.ceil(height / diamondSpacing) }, (_, i) => {
        const col = i % Math.ceil(width / diamondSpacing);
        const row = Math.floor(i / Math.ceil(width / diamondSpacing));
        const x = col * diamondSpacing + diamondOffsetX;
        const y = row * diamondSpacing + diamondOffsetY;
        if (x < width && y < height) {
          const points = `${x},${y - diamondSize} ${x + diamondSize},${y} ${x},${y + diamondSize} ${x - diamondSize},${y}`;
          return `<polygon points="${points}" fill="none" stroke="${baseColor}" stroke-width="0.5" opacity="${opacity}"/>`;
        }
        return '';
      }).filter(Boolean).join('');
      break;
      
    case 'stars':
      // Star pattern
      const numStars = 8 + Math.floor(Math.random() * 12);
      patternSvg = Array.from({ length: numStars }, () => {
        const x = Math.random() * width;
        const y = Math.random() * height;
        const size = 5 + Math.random() * 8;
        const outerRadius = size;
        const innerRadius = size * 0.4;
        const points = [];
        for (let i = 0; i < 10; i++) {
          const angle = (Math.PI / 5) * i - Math.PI / 2;
          const radius = i % 2 === 0 ? outerRadius : innerRadius;
          const px = x + radius * Math.cos(angle);
          const py = y + radius * Math.sin(angle);
          points.push(`${px},${py}`);
        }
        return `<polygon points="${points.join(' ')}" fill="none" stroke="${baseColor}" stroke-width="0.5" opacity="${opacity}"/>`;
      }).join('');
      break;
  }
  
  return `url("data:image/svg+xml,${encodeURIComponent(`
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="${patternId}" x="0" y="0" width="${width}" height="${height}" patternUnits="userSpaceOnUse">
          ${patternSvg}
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#${patternId})"/>
    </svg>
  `)}")`;
}